/**
 * Armadillo Security Monitor - Client-Side Attack Detection
 * Phoenix Architecture: Detects reconnaissance and scanning attempts at browser level
 * 
 * While client-side JS cannot detect nmap scans directly, it CAN:
 * - Monitor network timing anomalies
 * - Detect rapidly changing request patterns
 * - Identify suspicious browser behavior
 * - Track cross-origin attempts
 * - Detect timing-based attacks
 */

import { supabase } from '@/integrations/supabase/client';
import { armadilloAPI } from '@/services/armadillo';
import { logger } from '@/utils/logger';

interface SecurityAlert {
  type: string;
  severity: 'low' | 'medium' | 'high' | 'critical';
  message: string;
  timestamp: string;
  metadata?: Record<string, any>;
}

interface ScanDetectionResult {
  scan_detected: boolean;
  scan_type: string | null;
  risk_level: 'low' | 'medium' | 'high';
  details: string;
}

class SecurityMonitor {
  private static instance: SecurityMonitor;
  private requestTimeline: Map<string, number[]> = new Map();
  private requestCount = 0;
  private lastReset = Date.now();
  private readonly WINDOW_MS = 60000; // 1 minute window
  private readonly HIGH_FREQUENCY_THRESHOLD = 200; // requests per minute
  private readonly MEDIUM_FREQUENCY_THRESHOLD = 100;
  private alerts: SecurityAlert[] = [];
  private scanAttempts = 0;
  private lastAlertTime = 0;
  private readonly ALERT_COOLDOWN_MS = 5000; // 5 seconds between alerts

  static getInstance(): SecurityMonitor {
    if (!SecurityMonitor.instance) {
      SecurityMonitor.instance = new SecurityMonitor();
    }
    return SecurityMonitor.instance;
  }

  /**
   * Track a network request and analyze for anomalies
   */
  trackRequest(endpoint: string): ScanDetectionResult {
    const now = Date.now();
    
    // Reset counter if window expired
    if (now - this.lastReset > this.WINDOW_MS) {
      this.requestCount = 0;
      this.lastReset = now;
      this.requestTimeline.clear();
    }

    this.requestCount++;

    // Track per-endpoint requests
    if (!this.requestTimeline.has(endpoint)) {
      this.requestTimeline.set(endpoint, []);
    }
    const timestamps = this.requestTimeline.get(endpoint)!;
    timestamps.push(now);

    // Clean up old timestamps (keep only last 60 seconds)
    const cutoff = now - this.WINDOW_MS;
    const recentTimestamps = timestamps.filter(t => t > cutoff);
    this.requestTimeline.set(endpoint, recentTimestamps);

    // Detect high-frequency scanning
    if (this.requestCount > this.HIGH_FREQUENCY_THRESHOLD) {
      this.scanAttempts++;
      return {
        scan_detected: true,
        scan_type: 'high_frequency_scan',
        risk_level: 'high',
        details: `Unusual request frequency: ${this.requestCount} requests in ${Math.round((now - this.lastReset) / 1000)}s`
      };
    }

    if (this.requestCount > this.MEDIUM_FREQUENCY_THRESHOLD) {
      return {
        scan_detected: true,
        scan_type: 'elevated_request_rate',
        risk_level: 'medium',
        details: `Elevated request rate: ${this.requestCount} requests in window`
      };
    }

    // Detect endpoint scraping (rapid requests to different endpoints)
    if (this.requestTimeline.size > 30) {
      const rapidAccess = Array.from(this.requestTimeline.entries())
        .filter(([_, times]) => times.length > 5)
        .length;
      
      if (rapidAccess > 10) {
        return {
          scan_detected: true,
          scan_type: 'endpoint_scraping',
          risk_level: 'medium',
          details: `Possible endpoint enumeration: ${rapidAccess} endpoints with multiple requests`
        };
      }
    }

    return {
      scan_detected: false,
      scan_type: null,
      risk_level: 'low',
      details: 'Normal request pattern'
    };
  }

  /**
   * Check for browser-based reconnaissance
   */
  detectBrowserRecon(): ScanDetectionResult {
    try {
      const alerts: string[] = [];

      // Detect if DevTools is open (common for manual inspection)
      const devToolsOpen = (() => {
        const element = new Image();
        Object.defineProperty(element, 'id', {
          get: () => {
            alerts.push('DevTools detection triggered');
            return '';
          }
        });
        console.log('%c', element);
        return alerts.length > 0;
      })();

      // Detect console manipulation (common for XSS probing)
      const consoleMethods = ['log', 'warn', 'error', 'info', 'debug'] as const;
      for (const method of consoleMethods) {
        const original = console[method];
        (console as any)[method] = function() {
          // If someone is wrapping console, flag it
          if (new Error().stack?.includes('console-manipulation')) {
            alerts.push('Console manipulation detected');
          }
          return original.apply(console, arguments as any);
        };
      }

      if (alerts.length > 0) {
        return {
          scan_detected: true,
          scan_type: 'browser_reconnaissance',
          risk_level: 'medium',
          details: alerts.join('; ')
        };
      }

      return {
        scan_detected: false,
        scan_type: null,
        risk_level: 'low',
        details: 'No reconnaissance detected'
      };
    } catch {
      return {
        scan_detected: false,
        scan_type: null,
        risk_level: 'low',
        details: 'Recon check unavailable'
      };
    }
  }

  /**
   * Log a security alert
   */
  async logAlert(type: string, severity: 'low' | 'medium' | 'high' | 'critical', message: string, metadata?: Record<string, any>) {
    const now = Date.now();
    
    // Rate limit alerts to prevent noise
    if (now - this.lastAlertTime < this.ALERT_COOLDOWN_MS) {
      return;
    }
    this.lastAlertTime = now;

    const alert: SecurityAlert = {
      type,
      severity,
      message,
      timestamp: new Date().toISOString(),
      metadata
    };

    this.alerts.push(alert);

    // Keep only last 50 alerts
    if (this.alerts.length > 50) {
      this.alerts = this.alerts.slice(-50);
    }

    // Log to console
    const prefix = `🦔 [SecurityMonitor]`;
    switch (severity) {
      case 'critical':
        console.error(`${prefix} 🔴 ${message}`, metadata || '');
        break;
      case 'high':
        console.warn(`${prefix} 🟠 ${message}`, metadata || '');
        break;
      case 'medium':
        console.warn(`${prefix} 🟡 ${message}`, metadata || '');
        break;
      default:
        console.log(`${prefix} 🔵 ${message}`, metadata || '');
    }

    // Log to Supabase
    try {
      const { data: { user } } = await supabase.auth.getUser();
      await armadilloAPI.logSecurityEvent(
        user?.id || null,
        `scan_${type}`,
        severity,
        message,
        { ...metadata, source: 'client_security_monitor' }
      );

      // If high or critical, also create threat capsule
      if (severity === 'high' || severity === 'critical') {
        await armadilloAPI.createThreatCapsule(user?.id || null, {
          alert_type: type,
          severity,
          message,
          metadata,
          timestamp: alert.timestamp
        });
      }
    } catch (error) {
      console.error('Failed to log security alert:', error);
    }
  }

  /**
   * Check for known scan patterns in the environment
   */
  checkEnvironmentThreats(): ScanDetectionResult[] {
    const results: ScanDetectionResult[] = [];
    
    // Check for localStorage manipulation (common in XSS probing)
    try {
      const testKey = '__armadillo_test__';
      const originalSetItem = localStorage.setItem;
      const originalGetItem = localStorage.getItem;

      // Check if localStorage has been tampered with
      const proto = Object.getPrototypeOf(localStorage);
      if (proto.setItem !== originalSetItem || proto.getItem !== originalGetItem) {
        results.push({
          scan_detected: true,
          scan_type: 'storage_tampering',
          risk_level: 'high',
          details: 'localStorage API tampering detected'
        });
      }
    } catch {
      // Storage unavailable
    }

    // Check for unusual URL parameters (SQL injection probing)
    try {
      const params = new URLSearchParams(window.location.search);
      const suspiciousParams = ['id', 'select', 'union', 'drop', 'exec', 'eval', 
                               'cmd', 'command', 'exec', 'ping', 'wget'];
      
      for (const [key, value] of params) {
        if (suspiciousParams.includes(key.toLowerCase())) {
          results.push({
            scan_detected: true,
            scan_type: 'parameter_probing',
            risk_level: 'medium',
            details: `Suspicious URL parameter: ${key}=${value.substring(0, 50)}`
          });
          break;
        }
      }
    } catch {
      // URL parsing error
    }

    return results;
  }

  /**
   * Check for clickjacking or framing attempts
   */
  detectFramingAttack(): boolean {
    try {
      return window.self !== window.top;
    } catch {
      // Cross-origin access denied - this could mean we're in an iframe
      return true;
    }
  }

  /**
   * Get all recent alerts
   */
  getRecentAlerts(count: number = 10): SecurityAlert[] {
    return this.alerts.slice(-count);
  }

  /**
   * Get threat statistics
   */
  getStatistics() {
    const now = Date.now();
    const windowElapsed = Math.round((now - this.lastReset) / 1000);
    
    return {
      requestCount: this.requestCount,
      scanAttempts: this.scanAttempts,
      windowSeconds: Math.min(windowElapsed, 60),
      trackedEndpoints: this.requestTimeline.size,
      recentAlerts: this.getRecentAlerts(5),
      ratePerSecond: this.requestCount / Math.max(windowElapsed, 1)
    };
  }

  /**
   * Reset monitor state
   */
  reset() {
    this.requestTimeline.clear();
    this.requestCount = 0;
    this.lastReset = Date.now();
    this.scanAttempts = 0;
    this.alerts = [];
  }
}

export const securityMonitor = SecurityMonitor.getInstance();
export type { SecurityAlert, ScanDetectionResult };
